/**
 * Vue侧VSCode存储适配器
 * 负责Vue chatStore从localForage迁移到VSCode SQLite的桥接
 */

import userStore from './userStore';

/**
 * VSCode存储适配器类
 */
class VSCodeStorageAdapter {
	constructor() {
		this.isVSCodeEnvironment = typeof window !== 'undefined' && window.SharedServicesAccessor;
		this.isInitialized = false;
		this.migrationStatus = {
			isCompleted: false,
			lastMigrationTime: 0,
			migratedCount: 0,
			totalCount: 0
		};
		
		console.log('[VSCodeStorageAdapter] 初始化', {
			isVSCodeEnvironment: this.isVSCodeEnvironment
		});
	}

	/**
	 * 检查是否在VSCode环境中
	 */
	isInVSCodeEnvironment() {
		return this.isVSCodeEnvironment;
	}

	/**
	 * 获取SharedServicesAccessor实例
	 */
	getSharedServicesAccessor() {
		try {
			if (!window.SharedServicesAccessor) {
				console.warn('[VSCodeStorageAdapter] SharedServicesAccessor不存在');
				return null;
			}

			let accessor;
			if (typeof window.SharedServicesAccessor.getInstance === 'function') {
				accessor = window.SharedServicesAccessor.getInstance();
			} else if (typeof window.SharedServicesAccessor === 'object') {
				accessor = window.SharedServicesAccessor;
			} else {
				console.warn('[VSCodeStorageAdapter] SharedServicesAccessor格式不正确');
				return null;
			}

			return accessor;
		} catch (error) {
			console.error('[VSCodeStorageAdapter] 获取SharedServicesAccessor失败:', error);
			return null;
		}
	}

	/**
	 * 初始化适配器
	 */
	async initialize() {
		if (this.isInitialized) {
			return true;
		}

		try {
			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境中，跳过初始化');
				return false;
			}

			// 等待VSCode服务可用
			// await this.waitForVSCodeServices();
			
			this.isInitialized = true;
			console.log('[VSCodeStorageAdapter] 初始化完成');
			return true;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 初始化失败:', error);
			return false;
		}
	}

	/**
	 * 等待VSCode服务可用
	 */
	async waitForVSCodeServices(maxWaitTime = 5000) {
		return new Promise((resolve, reject) => {
			const startTime = Date.now();
			
			const checkServices = () => {
				try {
					// 检查SharedServicesAccessor是否存在
					if (!window.SharedServicesAccessor) {
						if (Date.now() - startTime > maxWaitTime) {
							reject(new Error('SharedServicesAccessor不存在，等待超时'));
							return;
						}
						setTimeout(checkServices, 100);
						return;
					}

					// 获取实例
					let accessor;
					if (typeof window.SharedServicesAccessor.getInstance === 'function') {
						accessor = window.SharedServicesAccessor.getInstance();
					} else if (typeof window.SharedServicesAccessor === 'object') {
						accessor = window.SharedServicesAccessor;
					} else {
						if (Date.now() - startTime > maxWaitTime) {
							reject(new Error('SharedServicesAccessor格式不正确'));
							return;
						}
						setTimeout(checkServices, 100);
						return;
					}

					// 检查是否已初始化
					if (accessor && typeof accessor.isInitialized === 'function' && accessor.isInitialized()) {
						resolve(true);
					} else if (Date.now() - startTime > maxWaitTime) {
						reject(new Error('VSCode服务等待超时'));
					} else {
						setTimeout(checkServices, 100);
					}
				} catch (error) {
					if (Date.now() - startTime > maxWaitTime) {
						reject(new Error(`VSCode服务检查失败: ${error.message}`));
					} else {
						setTimeout(checkServices, 100);
					}
				}
			};
			
			checkServices();
		});
	}

	/**
	 * 保存Vue聊天会话到VSCode存储
	 */
	async saveVueChatSession(chat, userId = null) {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，无法保存会话');
				return false;
			}

			const actualUserId = userId || userStore.state.userInfo?.id;
			if (!actualUserId || actualUserId === 'default') {
				console.warn('[VSCodeStorageAdapter] 用户ID无效，跳过保存操作，避免数据污染');
				return false;
			}
			
			// 转换Vue chat格式为适配器格式
			const adaptedChat = this.adaptVueChatFormat(chat);
			
			// 获取SharedServicesAccessor实例
			const accessor = this.getSharedServicesAccessor();
			if (!accessor || typeof accessor.syncVueChatsToVSCode !== 'function') {
				console.warn('[VSCodeStorageAdapter] syncVueChatsToVSCode方法不可用');
				return false;
			}
			
			// 通过SharedServicesAccessor保存
			const result = await accessor.syncVueChatsToVSCode([adaptedChat], actualUserId);
			
			console.log('[VSCodeStorageAdapter] Vue会话保存结果:', result);
			return result;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 保存Vue会话失败:', error);
			return false;
		}
	}

	/**
	 * 批量保存Vue聊天会话
	 */
	async saveMultipleVueChats(chats, userId = null) {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment || !chats || chats.length === 0) {
				return false;
			}

			const actualUserId = userId || userStore.state.userInfo?.id;
			if (!actualUserId || actualUserId === 'default') {
				console.warn('[VSCodeStorageAdapter] 用户ID无效，跳过批量保存操作，避免数据污染');
				return false;
			}
			
			// 转换所有聊天格式
			const adaptedChats = chats.map(chat => this.adaptVueChatFormat(chat));
			
			// 获取SharedServicesAccessor实例
			const accessor = this.getSharedServicesAccessor();
			if (!accessor || typeof accessor.syncVueChatsToVSCode !== 'function') {
				console.warn('[VSCodeStorageAdapter] syncVueChatsToVSCode方法不可用');
				return false;
			}
			
			// 批量保存
			const result = await accessor.syncVueChatsToVSCode(adaptedChats, actualUserId);
			
			console.log(`[VSCodeStorageAdapter] 批量保存${adaptedChats.length}个Vue会话:`, result);
			return result;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 批量保存Vue会话失败:', error);
			return false;
		}
	}

	/**
	 * 从VSCode存储加载Vue聊天数据
	 */
	async loadVueChatsFromVSCode(userId = null) {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，无法加载聊天数据');
				return null;
			}

			const actualUserId = userId || userStore.state.userInfo?.id;
			if (!actualUserId || actualUserId === 'default') {
				console.warn('[VSCodeStorageAdapter] 用户ID无效，跳过加载操作');
				return null;
			}
			
			// 获取SharedServicesAccessor实例
			const accessor = this.getSharedServicesAccessor();
			if (!accessor || typeof accessor.loadVueChatsFromVSCode !== 'function') {
				console.warn('[VSCodeStorageAdapter] loadVueChatsFromVSCode方法不可用');
				return null;
			}
			
			// 从VSCode存储加载Vue聊天数据
			const result = await accessor.loadVueChatsFromVSCode(actualUserId);
			
			console.log(`[VSCodeStorageAdapter] 从VSCode存储加载Vue聊天数据:`, result);
			return result;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 从VSCode存储加载Vue聊天数据失败:', error);
			return null;
		}
	}

	/**
	 * 新增：保存完整的Vue聊天数据到VSCode存储
	 */
	async saveVueChatsDataToVSCode(chatsData, userId = null) {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，无法保存聊天数据');
				return false;
			}

			const actualUserId = userId || userStore.state.userInfo?.id;
			if (!actualUserId || actualUserId === 'default') {
				console.warn('[VSCodeStorageAdapter] 用户ID无效，跳过保存操作，避免数据污染');
				return false;
			}
			
			// 获取SharedServicesAccessor实例
			const accessor = this.getSharedServicesAccessor();
			if (!accessor || typeof accessor.saveVueChatsToVSCode !== 'function') {
				console.warn('[VSCodeStorageAdapter] saveVueChatsToVSCode方法不可用');
				return false;
			}
			
			// 构建完整的聊天数据结构
			const completeChatsData = {
				chats: chatsData.chats || [],
				privateMsgMaxId: chatsData.privateMsgMaxId || 0,
				groupMsgMaxId: chatsData.groupMsgMaxId || 0,
				lastSync: Date.now()
			};
			
			// 保存到VSCode存储
			const result = await accessor.saveVueChatsToVSCode(actualUserId, completeChatsData);
			
			console.log(`[VSCodeStorageAdapter] 保存Vue聊天数据到VSCode存储:`, result);
			return result;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 保存Vue聊天数据到VSCode存储失败:', error);
			return false;
		}
	}

	/**
	 * 转换Vue chat格式为适配器期望的格式
	 */
	adaptVueChatFormat(chat) {
		return {
			targetId: chat.targetId,
			type: chat.type || 'PRIVATE',
			showName: chat.showName || chat.targetId,
			headImage: chat.headImage || '',
			lastContent: chat.lastContent || '',
			lastSendTime: chat.lastSendTime || Date.now(),
			unreadCount: chat.unreadCount || 0,
			messages: chat.messages || []
		};
	}

	/**
	 * 搜索所有消息（跨Vue和React平台）
	 */
	async searchAllMessages(query, options = {}) {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，无法搜索');
				return [];
			}

			// 获取SharedServicesAccessor实例
			const accessor = this.getSharedServicesAccessor();
			if (!accessor || typeof accessor.searchAllMessages !== 'function') {
				console.warn('[VSCodeStorageAdapter] searchAllMessages方法不可用');
				return [];
			}

			const results = await accessor.searchAllMessages(query, options);
			
			console.log(`[VSCodeStorageAdapter] 搜索"${query}"找到${results.length}条结果`);
			return results;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 搜索消息失败:', error);
			return [];
		}
	}

	/**
	 * 从localForage迁移数据到VSCode存储
	 */
	async migrateFromLocalForage(localForageData) {
		try {
			console.log('[VSCodeStorageAdapter] 开始从localForage迁移数据...');
			
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，跳过迁移');
				return { success: false, message: '不在VSCode环境中' };
			}

			// 检查是否已经迁移过
			if (this.migrationStatus.isCompleted) {
				console.log('[VSCodeStorageAdapter] 数据已迁移，跳过重复迁移');
				return { 
					success: true, 
					message: '数据已迁移', 
					...this.migrationStatus 
				};
			}

			// 修复：确保有真实的用户ID，避免使用默认值覆盖数据
			const userId = userStore.state.userInfo?.id;
			if (!userId || userId === 'default') {
				console.warn('[VSCodeStorageAdapter] 用户ID无效，跳过迁移操作，避免数据污染');
				return { success: false, message: '用户ID无效，无法执行迁移' };
			}
			
			// 提取聊天数据
			const chats = this.extractChatsFromLocalForage(localForageData);
			
			if (chats.length === 0) {
				console.log('[VSCodeStorageAdapter] 没有发现需要迁移的聊天数据');
				return { success: true, message: '没有数据需要迁移', migratedCount: 0, totalCount: 0 };
			}

			// 批量迁移到VSCode存储
			const migrationResult = await this.saveMultipleVueChats(chats, userId);
			
			if (migrationResult) {
				// 更新迁移状态
				this.migrationStatus = {
					isCompleted: true,
					lastMigrationTime: Date.now(),
					migratedCount: chats.length,
					totalCount: chats.length
				};
				
				// 保存迁移状态到localStorage
				localStorage.setItem('vscode_migration_status', JSON.stringify(this.migrationStatus));
				
				console.log('[VSCodeStorageAdapter] localForage迁移完成:', this.migrationStatus);
				return { 
					success: true, 
					message: '迁移成功', 
					...this.migrationStatus 
				};
			} else {
				console.error('[VSCodeStorageAdapter] localForage迁移失败');
				return { success: false, message: '迁移失败', migratedCount: 0, totalCount: chats.length };
			}

		} catch (error) {
			console.error('[VSCodeStorageAdapter] localForage迁移异常:', error);
			return { success: false, message: error.message, migratedCount: 0, totalCount: 0 };
		}
	}

	/**
	 * 从localForage数据中提取聊天会话
	 */
	extractChatsFromLocalForage(localForageData) {
		const chats = [];
		
		try {
			// localForage数据可能是各种格式，尝试解析
			if (Array.isArray(localForageData)) {
				// 如果是数组格式
				chats.push(...localForageData);
			} else if (localForageData && typeof localForageData === 'object') {
				// 如果是对象格式，尝试提取聊天数据
				if (localForageData.chats && Array.isArray(localForageData.chats)) {
					chats.push(...localForageData.chats);
				} else {
					// 遍历对象的值，寻找聊天数据
					Object.values(localForageData).forEach(value => {
						if (value && typeof value === 'object' && value.targetId) {
							chats.push(value);
						}
					});
				}
			}
			
			// 过滤和验证聊天数据
			return chats.filter(chat => 
				chat && 
				chat.targetId && 
				typeof chat.targetId === 'string'
			).map(chat => this.adaptVueChatFormat(chat));
			
		} catch (error) {
			console.error('[VSCodeStorageAdapter] 提取聊天数据失败:', error);
			return [];
		}
	}

	/**
	 * 获取迁移状态
	 */
	getMigrationStatus() {
		// 尝试从localStorage获取迁移状态
		try {
			const stored = localStorage.getItem('vscode_migration_status');
			if (stored) {
				this.migrationStatus = { ...this.migrationStatus, ...JSON.parse(stored) };
			}
		} catch (error) {
			console.warn('[VSCodeStorageAdapter] 获取迁移状态失败:', error);
		}
		
		return { ...this.migrationStatus };
	}

	/**
	 * 重置迁移状态
	 */
	resetMigrationStatus() {
		this.migrationStatus = {
			isCompleted: false,
			lastMigrationTime: 0,
			migratedCount: 0,
			totalCount: 0
		};
		
		try {
			localStorage.removeItem('vscode_migration_status');
		} catch (error) {
			console.warn('[VSCodeStorageAdapter] 清除迁移状态失败:', error);
		}
		
		console.log('[VSCodeStorageAdapter] 迁移状态已重置');
	}

	/**
	 * 获取FTS索引统计信息
	 */
	async getFTSIndexStats() {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				return { totalMessages: 0, indexSize: 0, lastUpdated: 0 };
			}

			const stats = await window.SharedServicesAccessor.getFTSIndexStats();
			console.log('[VSCodeStorageAdapter] FTS索引统计:', stats);
			return stats;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 获取FTS统计失败:', error);
			return { totalMessages: 0, indexSize: 0, lastUpdated: 0 };
		}
	}

	/**
	 * 重建FTS索引
	 */
	async rebuildFTSIndex() {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，无法重建索引');
				return false;
			}

			await window.SharedServicesAccessor.rebuildFTSIndex();
			console.log('[VSCodeStorageAdapter] FTS索引重建完成');
			return true;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] 重建FTS索引失败:', error);
			return false;
		}
	}

	/**
	 * 同步React线程数据
	 */
	async syncReactThreads() {
		try {
			if (!this.isInitialized) {
				await this.initialize();
			}

			if (!this.isVSCodeEnvironment) {
				console.warn('[VSCodeStorageAdapter] 不在VSCode环境，无法同步React线程');
				return false;
			}

			const result = await window.SharedServicesAccessor.syncReactThreadsToVSCode();
			console.log('[VSCodeStorageAdapter] React线程同步结果:', result);
			return result;

		} catch (error) {
			console.error('[VSCodeStorageAdapter] React线程同步失败:', error);
			return false;
		}
	}
}

// 创建全局单例
const vscodeStorageAdapter = new VSCodeStorageAdapter();

export default vscodeStorageAdapter; 